import { cache } from 'react'
import qs from 'qs'

import { apiService, createRequestHeadersConfig } from 'shared/api'
import { distributionCenterId as defaultDistributionCenterId } from 'shared/config/distributionCenter'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'

import {
  ProductMetaTypes,
  ProductsListResponseTypes,
  ProductTypes,
  RecommendedProductsResponseTypes
} from '../types/products'
import {
  stripHeavyProductCategoryContent,
  stripHeavyProductCategoryContentFromList
} from '../utils/categories'
import { buildProductIdsQueryString } from '../utils/products'

const api = apiService()

type QueryProps = {
  page?: string
  limit?: string
  category?: string
}

type getProductsByCategoryIdPayload = {
  token?: string
  slug: string
  query?: QueryProps
  distributionCenterId?: string
}

type GetSingleProductPayload = {
  slug: string
  token?: string
  distributionCenterId?: string
}

type GetProductGroupByIdPayload = {
  id: string
  token?: string
  query?: QueryProps
}

export const getSingleProduct = async (
  payload: GetSingleProductPayload
): Promise<{ data: { data: ProductTypes } }> => {
  const { token, slug, distributionCenterId = defaultDistributionCenterId } = payload

  const response = (await api.get(
    `/products/${slug}/${distributionCenterId}`,
    createRequestHeadersConfig(token)
  )) as { data: { data: ProductTypes } }

  return {
    data: {
      data: stripHeavyProductCategoryContent(response.data.data)
    }
  }
}

type GetMultipleProductsByIdsPayload = {
  productIds: string[]
  token?: string
  distributionCenterId: string
  query?: QueryProps
}

export interface GetMultipleProductsByIdsResponse {
  data: {
    data: ProductTypes[]
    meta: ProductMetaTypes
  }
}

export const getMultipleProductsByIds = async (
  payload: GetMultipleProductsByIdsPayload
): Promise<GetMultipleProductsByIdsResponse> => {
  const { token, distributionCenterId, productIds = [], query = {} } = payload
  const params: string = buildProductIdsQueryString(productIds)

  const response = (await api.get(
    `/products/by-ids/${distributionCenterId}?${params}&${qs.stringify(query)}`,
    createRequestHeadersConfig(token)
  )) as GetMultipleProductsByIdsResponse

  return {
    data: {
      ...response.data,
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

interface GetNextMultipleProductsByIdsPayload {
  productIds?: string[]
  token?: string
  limit?: number
  query?: QueryProps
  distributionCenterId: string
}

/**
 * Infinite-scroll page fetcher for the Algolia search results page.
 * The Algolia result IDs are resolved up-front; this slices them per page
 * and resolves product details for the requested slice.
 */
export const getNextMultipleProductsByIdsPage = async (
  pageParam = 1,
  {
    productIds = [],
    query,
    token,
    limit,
    distributionCenterId
  }: GetNextMultipleProductsByIdsPayload
): Promise<ProductTypes[]> => {
  const responseData = await getMultipleProductsByIds({
    productIds,
    token: token || '',
    distributionCenterId: distributionCenterId ?? '',
    query: {
      ...query,
      limit: limit?.toString() ?? PRODUCTS_LISTING_LIMIT.toString(),
      page: pageParam.toString()
    }
  })

  return responseData.data.data
}

export const fetchProductGroupById = async (
  payload: GetProductGroupByIdPayload
): Promise<{ data: { data: ProductTypes[] } }> => {
  const { token, id, query = {} } = payload

  const params = { page: 1, limit: 10, ...query }

  const response = (await api.get(
    `/product-groups/${id}?${qs.stringify(params)}`,
    createRequestHeadersConfig(token)
  )) as { data: { data: ProductTypes[] } }

  return {
    data: {
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

export const getProductsByCategoryId = async (
  payload: getProductsByCategoryIdPayload
): Promise<ProductsListResponseTypes> => {
  const { slug, query = {}, token, distributionCenterId = defaultDistributionCenterId } = payload
  const params = { page: 1, limit: 20, ...query }
  // @sieradz - remove  sort_by: 'name', order: 'asc' from params
  // because the API gives us the products in order of display (display order)

  const response = (await api.get(
    `/product-categories/${slug}/products/${distributionCenterId}?${qs.stringify(
      params
    )}`,
    createRequestHeadersConfig(token)
  )) as ProductsListResponseTypes

  return {
    data: {
      ...response.data,
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

interface GetProductSubstitutesByIdPayload {
  productId: string
  token?: string
  distributionCenterId?: string
}

interface GetProductSubstitutesByIdResponse {
  data: {
    data: ProductTypes[]
  }
}

export const getProductSubstitutesById = async (
  payload: GetProductSubstitutesByIdPayload
): Promise<GetProductSubstitutesByIdResponse> => {
  const { token, productId, distributionCenterId = defaultDistributionCenterId } = payload

  const response = (await api.get(
    `/products/${productId}/substitutes/${distributionCenterId}`,
    createRequestHeadersConfig(token)
  )) as GetProductSubstitutesByIdResponse

  return {
    data: {
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

type GetRecommendedProductsByCategoryPayload = {
  token?: string
  categoryId: string
  productId: string
}

type GetRecommendedProductsByCategoryResponse = {
  data: {
    data: ProductTypes[]
  }
}

export const getRecommendedProductsByCategory = async (
  payload: GetRecommendedProductsByCategoryPayload
): Promise<GetRecommendedProductsByCategoryResponse> => {
  const { categoryId, productId, token } = payload

  const response = (await api.get(
    `/product-categories/${categoryId}/product/${productId}/recommended-products`,
    createRequestHeadersConfig(token)
  )) as GetRecommendedProductsByCategoryResponse

  return {
    data: {
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

export const getRecommendedProductsByIds = async (
  productIds: string[],
  distributionCenterId: string,
  token: string
) => {
  const recommendedProducts: RecommendedProductsResponseTypes =
    await getMultipleProductsByIds({ productIds, distributionCenterId, token })

  return recommendedProducts?.data?.data || []
}

interface FavouritesPayload {
  id: string
  token?: string
}

export const addProductToFavourites = (payload: FavouritesPayload) => {
  const { id, token } = payload

  return api.post(
    `/favourite-products/${id}`,
    {},
    createRequestHeadersConfig(token)
  )
}

export const removeProductFromFavourites = (payload: FavouritesPayload) => {
  const { id, token } = payload

  return api.delete(`/favourite-products/${id}`, createRequestHeadersConfig(token))
}

export const fetchFavouriteProducts = async ({
  token,
  distributionCenterId,
  query
}: {
  token?: string
  distributionCenterId?: string
  query: UpsellingSectionQueryParams
}): Promise<ProductsListResponseTypes> => {
  const params = { page: 1, limit: 999, ...query }

  const response = (await api.get(
    `/favourite-products/${distributionCenterId || ''}${qs.stringify(params, {
      addQueryPrefix: true
    })}`,
    createRequestHeadersConfig(token)
  )) as ProductsListResponseTypes

  return {
    data: {
      ...response.data,
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

export const getNextFavouriteProductsPage = async (
  pageParam = 1,
  {
    query,
    token,
    limit
  }: { token?: string; query: UpsellingSectionQueryParams; limit: number }
) => {
  const response = await fetchFavouriteProducts({
    token,
    query: {
      ...query,
      page: pageParam.toString(),
      limit: limit.toString()
    }
  })

  return response.data.data
}

interface FetchLandingUpsellingResponse {
  data: {
    data: {
      bestsellers: ProductTypes[]
      newest: ProductTypes[]
      promoted: ProductTypes[]
    }
    meta: {
      entityType: string
      total: number
      lastPage: number
      currentPage: number
    }
  }
}

interface FetchFrequentlyBoughtResponse {
  data: {
    data: ProductTypes[]
    meta: {
      total: number
      lastPage: number
      currentPage: number
    }
  }
}

export interface FetchUpsellingResponse {
  data: {
    data: ProductTypes[]
    meta: {
      entityType: string
      total: number
      lastPage: number
      currentPage: number
    }
  }
}

interface UpsellingSectionQueryParams {
  page?: string | string[]
  limit?: string
  sort_by?: string
  order?: string
  type?: string
}

export const fetchUpsellingSection = async ({
  token,
  query
}: {
  token?: string
  query: UpsellingSectionQueryParams
}): Promise<FetchUpsellingResponse> => {
  const params = { page: 1, ...query }

  const response = (await api.get(
    `/products/upselling${qs.stringify(params, { addQueryPrefix: true })}`,
    createRequestHeadersConfig(token)
  )) as FetchUpsellingResponse

  return {
    data: {
      ...response.data,
      data: stripHeavyProductCategoryContentFromList(response.data.data)
    }
  }
}

export const getNextUpsellingPage = async (
  pageParam = 1,
  { query, token }: { token?: string; query: UpsellingSectionQueryParams }
) => {
  const responseData = await fetchUpsellingSection({
    token,
    query: {
      ...query,
      page: pageParam.toString()
    }
  })

  const nextProductsData = responseData.data.data

  return nextProductsData
}

export const fetchLandingUpsellingSections = async ({
  token
}: {
  token?: string
}): Promise<FetchLandingUpsellingResponse> => {
  const response = (await api.get(
    `/products/upselling?limit=10`,
    createRequestHeadersConfig(token)
  )) as FetchLandingUpsellingResponse

  return {
    data: {
      ...response.data,
      data: {
        bestsellers: stripHeavyProductCategoryContentFromList(
          response.data.data.bestsellers
        ),
        newest: stripHeavyProductCategoryContentFromList(
          response.data.data.newest
        ),
        promoted: stripHeavyProductCategoryContentFromList(
          response.data.data.promoted
        )
      }
    }
  }
}

export const fetchUpsellingSectionsQuery = async (
  type: string,
  token?: string
) => {
  const response = await fetchUpsellingSection({ token, query: { type } })

  return response.data.data
}

export const fetchLandingUpsellingSectionsQuery = async (token?: string) => {
  const response = await fetchLandingUpsellingSections({ token })

  return response.data.data
}

const mostFrequentlyBoughtDefaultResponse = {
  data: [],
  meta: {
    total: 0,
    lastPage: 1,
    currentPage: 1
  }
}

export const fetchMostFrequentlyBoughtProducts = async ({
  token,
  query
}: {
  token?: string
  query: UpsellingSectionQueryParams
}): Promise<FetchFrequentlyBoughtResponse> => {
  if (token) {
    const params = { page: 1, ...query }

    const response = (await api.get(
      `/most-frequently-bought${qs.stringify(params, {
        addQueryPrefix: true
      })}`,
      createRequestHeadersConfig(token)
    )) as FetchFrequentlyBoughtResponse

    return {
      data: {
        ...response.data,
        data: stripHeavyProductCategoryContentFromList(response.data.data)
      }
    }
  } else {
    return new Promise(resolve => {
      resolve({
        data: mostFrequentlyBoughtDefaultResponse
      })
    })
  }
}

export const getNextFrequentlyBoughtPage = async (
  pageParam = 1,
  { query, token }: { token?: string; query: UpsellingSectionQueryParams }
) => {
  const responseData = await fetchMostFrequentlyBoughtProducts({
    token,
    query: {
      ...query,
      page: pageParam.toString()
    }
  })

  const nextProductsData = responseData.data.data

  return nextProductsData
}

export const fetchMostFrequentlyBoughtProductsLanding = async ({
  token
}: {
  token?: string
}): Promise<FetchFrequentlyBoughtResponse> => {
  if (token) {
    const response = (await api.get(
      `/most-frequently-bought?limit=20`,
      createRequestHeadersConfig(token)
    )) as FetchFrequentlyBoughtResponse

    return {
      data: {
        ...response.data,
        data: stripHeavyProductCategoryContentFromList(response.data.data)
      }
    }
  } else {
    return new Promise(resolve => {
      resolve({
        data: mostFrequentlyBoughtDefaultResponse
      })
    })
  }
}

export const fetchMostFrequentlyBoughtProductsQuery = async (
  token?: string
) => {
  const response = await fetchMostFrequentlyBoughtProductsLanding({ token })

  return response.data.data
}

export const getProductGroupByIdQuery = async (
  token: string | undefined,
  id: string,
  query?: QueryProps
) => {
  const response = await fetchProductGroupById({ token, id, query })

  return response.data?.data || []
}

type getAllProductsPayload = {
  token?: string
  distributionCenterId?: string
  query?: QueryProps
}

export const getAllProducts = cache(
  async (
    payload: getAllProductsPayload
  ): Promise<ProductsListResponseTypes> => {
    const { query = {}, token, distributionCenterId = '' } = payload
    const params = { page: 1, ...query }
    const url = `/products/list/${distributionCenterId}${qs.stringify(params, {
      addQueryPrefix: true
    })}`

    const response = (await api.get(
      url,
      createRequestHeadersConfig(token)
    )) as ProductsListResponseTypes

    return {
      data: {
        ...response.data,
        data: stripHeavyProductCategoryContentFromList(response.data.data)
      }
    }
  }
)

interface GetNextAllProductsPayload {
  distributionCenterId?: string
  query?: QueryProps
  token?: string
  limit?: number
}

export const getNextAllProductsPage = async (
  pageParam = 1,
  { distributionCenterId, query, token, limit }: GetNextAllProductsPayload
) => {
  const responseData = await getAllProducts({
    token: token || '',
    distributionCenterId: distributionCenterId || '',
    query: {
      ...query,
      limit: limit?.toString() ?? PRODUCTS_LISTING_LIMIT.toString(),
      page: pageParam.toString()
    }
  })
  const nextProductsData = responseData.data.data

  return nextProductsData
}
